import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { groupByResource } from '@/features/auth/permissions'
import { useAuth } from '@/features/auth/store'
import { RoleDashboard } from '@/routes/analytics/RoleDashboard'

const ACTION_ORDER = ['create', 'read', 'update', 'delete'] as const

const ACTION_STYLE: Record<string, string> = {
  create: 'bg-success/10 text-success',
  read: 'bg-info/10 text-info',
  update: 'bg-warning/10 text-warning',
  delete: 'bg-danger/10 text-danger',
}

function labelFor(resource: string) {
  return resource.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
}

/**
 * The home screen: a role dashboard for anybody who can read analytics, a summary of the
 * account for anybody who cannot.
 *
 * An employee or a guest holds nothing on `sales_analytics`, so a dashboard would be five
 * panels of 403 for them. Showing them what their account is allowed to do is more use than
 * an empty page, and it is still the fastest way to check the permission matrix against the
 * PRD without reading a database.
 */
export function Dashboard() {
  const user = useAuth((s) => s.user)
  const canReadAnalytics = useAuth((s) => s.can)('sales_analytics', 'read')
  if (!user) return null
  if (canReadAnalytics) return <RoleDashboard />

  const grouped = groupByResource(user.permissions)
  const resources = Object.keys(grouped).sort()

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">Welcome, {user.full_name}</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Signed in as {user.email}
          {user.last_login_at &&
            ` · last signed in ${new Date(user.last_login_at).toLocaleString()}`}
        </p>
      </header>

      <div className="grid gap-4 tablet:grid-cols-3">
        <Card>
          <CardBody>
            <p className="text-xs uppercase tracking-wide text-slate-500">Roles</p>
            <p className="mt-1 text-2xl font-semibold">{user.roles.length}</p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {user.roles.map((r) => r.name).join(', ') || 'None assigned'}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-xs uppercase tracking-wide text-slate-500">Permissions</p>
            <p className="mt-1 text-2xl font-semibold">{user.permissions.length}</p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              across {resources.length} areas
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-xs uppercase tracking-wide text-slate-500">Account</p>
            <p className="mt-1 text-2xl font-semibold text-success">
              {user.is_active ? 'Active' : 'Disabled'}
            </p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Created {new Date(user.created_at).toLocaleDateString()}
            </p>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="What this account can do"
          description="Resolved from the PRD §9 permission matrix. The server enforces every one of these on each request."
        />
        <CardBody className="space-y-3">
          {resources.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              This account has no permissions. Ask an administrator to assign a role.
            </p>
          ) : (
            resources.map((resource) => (
              <div
                key={resource}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2 last:border-0 last:pb-0 dark:border-slate-800"
              >
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  {labelFor(resource)}
                </span>
                <span className="flex gap-1.5">
                  {ACTION_ORDER.filter((action) => grouped[resource].includes(action)).map(
                    (action) => (
                      <span
                        key={action}
                        className={`rounded px-2 py-0.5 text-xs font-medium ${ACTION_STYLE[action]}`}
                      >
                        {action}
                      </span>
                    ),
                  )}
                </span>
              </div>
            ))
          )}
        </CardBody>
      </Card>
    </div>
  )
}
